import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// MembersDock.tsx -- thin left-rail panel listing project members.
//
// Per user spec (workbench C.4 / I15):
//   - Width ~64px, full window height. Replaces the +Members modal as
//     the primary surface for picking a peer to DM.
//   - One row per member: circular initials avatar, name on hover.
//   - Unread badge in the top-right corner of each avatar -- summed
//     from session.unreadByUser across every session this user has
//     with the dock-row's userId.
//   - Per-member sound toggle (speaker icon) below each avatar; off
//     state mutes the bubble-pop SFX for incoming from that peer.
//     Default ON; persisted in localStorage via utils/notify.
//   - Bubble-pop SFX on incoming: when poll detects unreadByMember
//     went UP since the last tick, play playBubblePop({memberId}).
//     Honours per-member mute and global DND.
//   - Auto-pop AssistantWindow on incoming when DND is OFF: the
//     same delta detection that triggers sound also opens the
//     window so the user sees the new message immediately.
//   - Search bar at the top, shown only when the member list overflows
//     vertically (otherwise the avatars get all the space).
//
// Future (separate pushes, captured in workbench):
//   - Online/offline visual state via activity-status token (v1)
//   - Beyond-status rich activity stream (v2)
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../utils/api.js';
import { useChatStore } from '../../hooks/useAgent.js';
import { useAuth } from '../../hooks/useAuth.js';
import { isDndActive, isMemberMuted, playBubblePop, setMemberMuted, } from '../../utils/notify.js';
import { subscribeToSessions } from '../../utils/wireSubscribe.js';
import { RecipientHistoryPopover } from '../Chat/RecipientHistoryPopover.js';
// Polling cadence. With the SSE wake-up channel doing the
// heavy lifting (sub-second delivery of session-advanced
// events), polling is now a STRICT BACKSTOP for the case where
// the SSE drops (network blip, raindb restart, sleeping tab
// wake). 30s catches up cleanly without contributing to steady-
// state load -- each tick is one /sessions list refetch that
// returns 200 with the same shape regardless of activity.
//
// Going lower than 30s here doesn't help perceived freshness
// (SSE is faster), and going higher than 30s creates a visible
// gap when SSE is genuinely down. 30s is the right floor.
const POLL_INTERVAL_MS = 30000;
function getInitials(s) {
    if (!s)
        return '?';
    const parts = s.trim().split(/\s+/);
    if (parts.length === 1)
        return s.slice(0, 2).toUpperCase();
    return (parts[0][0] ?? '?').toUpperCase() + (parts[parts.length - 1][0] ?? '').toUpperCase();
}
export function MembersDock({ projectId }) {
    const { user } = useAuth();
    const [members, setMembers] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [filter, setFilter] = useState('');
    // Bumped whenever the user toggles a member's mute. Cheap signal so
    // the speaker-icon UI re-renders without a heavyweight subscription
    // model (the localStorage layer is the source of truth).
    const [muteTick, setMuteTick] = useState(0);
    // Previous unread snapshot keyed by memberId. We compare each poll
    // against this to detect "count went up since last tick" and fire
    // the bubble-pop SFX (honouring mute + DND).
    const previousUnreadRef = useRef(null);
    // Previous lastMessageAt snapshot keyed by sessionId. Drives the
    // chatStore.notifySessionAdvanced dispatch so windows tracking
    // that specific session refresh / restore.
    const previousSessionTsRef = useRef(null);
    const [needsSearch, setNeedsSearch] = useState(false);
    // History popover state. Per user direction (May 2026): a single
    // click on a member opens the per-recipient HISTORY (cross-session
    // list scoped to viewer+this member), NOT a chat window. The mental
    // model is "conversations are projects/topics, not running streams" --
    // members are entry points to PICK the topic you want to resume.
    // The user opens an actual chat window from within History (or via
    // the "+ New conversation" button at its footer).
    const [historyForMember, setHistoryForMember] = useState(null);
    const [historyAnchor, setHistoryAnchor] = useState(null);
    // Combined fetch: members list + session list. Members rarely
    // changes; sessions polls so unread badges stay current.
    const refreshAll = useCallback(async () => {
        try {
            const [mr, sr] = await Promise.all([
                api.get(`/p/${projectId}/members`),
                api.get(`/p/${projectId}/chat/sessions`),
            ]);
            setMembers(mr.members ?? []);
            setSessions(sr.sessions ?? []);
        }
        catch {
            // Non-fatal: keep showing the last successful snapshot.
        }
    }, [projectId]);
    // Sessions-only poll keeps unread counts fresh without re-fetching
    // the (mostly-static) member list every tick.
    const refreshSessionsOnly = useCallback(async () => {
        try {
            const sr = await api.get(`/p/${projectId}/chat/sessions`);
            setSessions(sr.sessions ?? []);
        }
        catch {
            // ignore
        }
    }, [projectId]);
    useEffect(() => {
        void refreshAll();
        const t = window.setInterval(() => void refreshSessionsOnly(), POLL_INTERVAL_MS);
        return () => window.clearInterval(t);
    }, [refreshAll, refreshSessionsOnly]);
    // SSE wake-up: open a persistent server-sent-events stream to
    // the BFF, subscribed to every currently-known session id.
    // When raindb's wire layer detects a new message on any of
    // those sessions (within ~200-400ms of the writer's POST),
    // the BFF forwards a `session-advanced` event and we
    // refreshSessionsOnly immediately -- the existing per-tick
    // delta detection (unread bumps, notifySessionAdvanced)
    // then runs against the freshened session list, giving
    // sub-second perceived latency end-to-end.
    //
    // The 8s polling above stays as a backstop: if the SSE
    // drops (network blip, raindb restart), the next polling
    // tick still catches up. SSE is a fast path, not a
    // single-point-of-failure replacement.
    //
    // Subscription churn: when the session set changes (new
    // session created, or user navigates such that sessions
    // are added/removed), the effect re-runs and reopens with
    // the new key set. Each reopen is one HTTP request --
    // negligible.
    const sessionIdsKey = useMemo(() => sessions.map((s) => s.sessionId).filter(Boolean).sort().join(','), [sessions]);
    useEffect(() => {
        if (!projectId)
            return undefined;
        if (!sessionIdsKey)
            return undefined;
        const ids = sessionIdsKey.split(',').filter(Boolean);
        if (ids.length === 0)
            return undefined;
        const cancel = subscribeToSessions(projectId, ids, (ev) => {
            // FAST PATH: route the SSE event STRAIGHT to the
            // chatStore. The event already names the sessionId
            // that advanced; there's no need to refetch the
            // sessions list first + wait for React re-render +
            // wait for the per-tick delta-detection useEffect.
            //
            // chatStore.notifySessionAdvanced finds the window
            // tracking this sessionId and calls pollNew()
            // immediately, which fetches just the new messages
            // via asc-poll (the asc head-check fast path on
            // the raindb side makes this a single sub-100ms
            // ObjectCache hit + S3 LIST + readLatest).
            //
            // The sessions-list refresh ALSO happens, in
            // parallel, so the unread badge math + bubble-pop
            // SFX still run. But the chat content doesn't
            // wait on it.
            useChatStore.getState().notifySessionAdvanced(ev.sessionId);
            void refreshSessionsOnly();
        }, (err) => {
            // eslint-disable-next-line no-console
            console.warn('[wire-subscribe] SSE error (polling fallback still active):', err.message);
        });
        return cancel;
    }, [projectId, sessionIdsKey, refreshSessionsOnly]);
    // Detect overflow to decide whether to show the search input. Each
    // member row is ~64px tall; the dock is full-height. If
    // members.length exceeds the visible-rows budget we surface the
    // search.
    useEffect(() => {
        const measure = () => {
            const slot = window.innerHeight - 16;
            const rowsThatFit = Math.floor(slot / 64);
            setNeedsSearch(members.length > rowsThatFit);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [members.length]);
    // Compute unread count per member from sessions data.
    // unread = sum of session.unreadByUser[viewer.userId] across every
    // session where the dock row's userId is the OTHER party.
    const unreadByMember = useMemo(() => {
        const map = new Map();
        if (!user)
            return map;
        const viewerId = user.userId;
        for (const s of sessions) {
            const myUnread = s.unreadByUser?.[viewerId];
            if (!myUnread || myUnread <= 0)
                continue;
            // Identify the "other party" of this session.
            const ownerId = s.userId;
            const otherUserIds = [];
            if (ownerId && ownerId !== viewerId) {
                otherUserIds.push(ownerId);
            }
            for (const p of s.participants ?? []) {
                if (p.type === 'user' && p.id !== viewerId) {
                    otherUserIds.push(p.id);
                }
            }
            // Add the unread count to each other-party member. For 1:1 DMs
            // this is one entry; group threads (future) will distribute the
            // count per peer the same way.
            for (const oid of new Set(otherUserIds)) {
                map.set(oid, (map.get(oid) ?? 0) + myUnread);
            }
        }
        return map;
    }, [sessions, user]);
    const visibleMembers = useMemo(() => {
        const me = user?.userId;
        const list = members.filter((m) => m.userId !== me); // hide self from the dock
        if (!filter.trim())
            return list;
        const needle = filter.trim().toLowerCase();
        return list.filter((m) => (m.name ?? '').toLowerCase().includes(needle)
            || (m.email ?? '').toLowerCase().includes(needle));
    }, [members, filter, user]);
    // Notification effect: detect new server-side activity per poll
    // tick at TWO granularities:
    //
    // 1. Per-member unread bump -> play bubble-pop SFX (honouring
    //    mute + DND). The SFX is the "you have a new message"
    //    affordance regardless of whether a window is already open.
    //
    // 2. Per-session lastMessageAt advance -> dispatch
    //    chatStore.notifySessionAdvanced(sessionId). The store routes
    //    that to any windows already tracking that session: visible
    //    windows refetch history, minimized windows bump
    //    inlineUnread (DND or no DND -- restoring is the user's
    //    choice). This is the multi-window equivalent of the legacy
    //    "auto-pop the AssistantWindow" behaviour.
    //
    // First-poll is a no-op for both refs so existing state at page
    // load doesn't trigger a fanfare of beeps + window-popups.
    useEffect(() => {
        // ---- per-member sound dispatch ----
        const prevUnread = previousUnreadRef.current;
        if (prevUnread !== null) {
            for (const [memberId, count] of unreadByMember) {
                const before = prevUnread.get(memberId) ?? 0;
                if (count > before) {
                    playBubblePop({ memberId });
                }
            }
        }
        previousUnreadRef.current = new Map(unreadByMember);
        // ---- per-session route dispatch ----
        const prevTs = previousSessionTsRef.current;
        if (prevTs !== null) {
            for (const s of sessions) {
                if (!s.sessionId)
                    continue;
                const beforeTs = prevTs.get(s.sessionId);
                const nowTs = s.lastMessageAt ?? '';
                if (nowTs && nowTs !== beforeTs) {
                    // Either new session in this poll (no prior ts) or its
                    // lastMessageAt advanced. Notify the store so any windows
                    // tracking this sessionId refresh / accumulate unread.
                    useChatStore.getState().notifySessionAdvanced(s.sessionId);
                }
            }
        }
        const nextTs = new Map();
        for (const s of sessions) {
            if (s.sessionId) {
                nextTs.set(s.sessionId, s.lastMessageAt ?? '');
            }
        }
        previousSessionTsRef.current = nextTs;
    }, [unreadByMember, sessions]);
    // Suppress DND-related auto-pop unused warning. DND is now consulted
    // inside chatStore.notifySessionAdvanced (when restoring minimized
    // windows). The per-member sound check above also implicitly
    // honours DND via playBubblePop's internal isDndActive() check.
    void isDndActive;
    const onPickMember = useCallback((m, ev) => {
        if (m.status !== 'active')
            return;
        // Per user direction (May 2026): single-click a member -> open the
        // History popover scoped to this member (cross-session list of
        // topic-based conversations with them). The previous behavior --
        // auto-resuming or opening a chat draft -- conflated "I want to
        // talk to this person" with "I want to RESUME a SPECIFIC topic with
        // this person", which is the wrong default for FDN's
        // topic/project-based messaging model. Conversations here are not
        // running streams; they are scoped to a specific subject. The user
        // picks the subject from History (or starts a new one with the
        // "+ New conversation" button inside the popover).
        const rect = ev.currentTarget.getBoundingClientRect();
        setHistoryAnchor({ x: rect.x, y: rect.y, width: rect.width, height: rect.height });
        setHistoryForMember(m);
    }, []);
    const historyRecipient = useMemo(() => {
        if (!historyForMember)
            return null;
        return {
            kind: 'user',
            userId: historyForMember.userId,
            name: historyForMember.name ?? historyForMember.email ?? historyForMember.userId,
            ...(historyForMember.email ? { email: historyForMember.email } : {}),
        };
    }, [historyForMember]);
    // Don't render the dock if there are no other members yet -- avoids
    // an empty rail dominating the desktop in single-user demos.
    if (visibleMembers.length === 0)
        return null;
    return (_jsxs(_Fragment, { children: [_jsxs("aside", { className: "members-dock", "aria-label": "Project members", children: [needsSearch && (_jsx("div", { className: "members-dock-search-wrap", children: _jsx("input", { type: "text", className: "members-dock-search", placeholder: '\uD83D\uDD0D', value: filter, onChange: (e) => setFilter(e.target.value), title: "Filter members by name or email" }) })), _jsx("div", { className: "members-dock-list", children: visibleMembers.map((m) => {
                            const unread = unreadByMember.get(m.userId) ?? 0;
                            const display = m.name ?? m.email ?? m.userId;
                            const dimmed = m.status !== 'active';
                            // Read mute state on every render. Cheap (one localStorage
                            // read per visible member); muteTick forces a recompute when
                            // the toggle is clicked. Wrapping in a separate state would
                            // require subscribing to localStorage events which adds more
                            // ceremony than the current member counts justify.
                            void muteTick;
                            const muted = isMemberMuted(m.userId);
                            return (_jsxs("div", { className: `members-dock-cell ${dimmed ? 'dimmed' : ''}`, children: [_jsxs("button", { type: "button", className: "members-dock-row", onClick: (e) => onPickMember(m, e), disabled: dimmed, title: `${display}${unread > 0 ? ` -- ${unread} unread` : ''}`, "aria-label": `Open conversation history with ${display}${unread > 0 ? `, ${unread} unread` : ''}`, children: [_jsx("span", { className: "members-dock-avatar", "aria-hidden": true, children: getInitials(display) }), unread > 0 && (_jsx("span", { className: "members-dock-unread", "aria-hidden": true, children: unread > 99 ? '99+' : unread }))] }), _jsx("button", { type: "button", className: `members-dock-mute ${muted ? 'muted' : ''}`, onClick: (e) => {
                                            e.stopPropagation();
                                            setMemberMuted(m.userId, !muted);
                                            setMuteTick((n) => n + 1);
                                        }, title: muted
                                            ? `Unmute notifications from ${display}`
                                            : `Mute notifications from ${display}`, "aria-label": muted ? 'Unmute' : 'Mute', "aria-pressed": muted, children: muted ? '\uD83D\uDD07' : '\uD83D\uDD08' })] }, m.userId));
                        }) })] }), historyRecipient && projectId && (_jsx(RecipientHistoryPopover, { projectId: projectId, recipient: historyRecipient, open: historyForMember !== null, anchorRect: historyAnchor, onClose: () => {
                    setHistoryForMember(null);
                    setHistoryAnchor(null);
                } }))] }));
}
